/**
 * AdminAnalytics — admin dashboard for page-view analytics.
 * Route: /admin/analytics (editor + admin gate on the backend).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, TrendingUp, Users, Globe, Loader2 } from "lucide-react";

const RANGES = [
  { key: 7,  label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
];

async function fetchOverview(days) {
  const t = localStorage.getItem("sp_token") || localStorage.getItem("token") || "";
  const r = await fetch(
    `${process.env.REACT_APP_BACKEND_URL || ""}/api/analytics/admin/overview?days=${days}`,
    { headers: t ? { Authorization: `Bearer ${t}` } : {} },
  );
  if (!r.ok) throw new Error(`Analytics fetch failed (${r.status})`);
  return r.json();
}

function StatCard({ icon: Icon, label, value, sub, tint = "blue" }) {
  const tints = {
    blue:  "bg-blue-50 text-blue-700 border-blue-100",
    green: "bg-green-50 text-green-700 border-green-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tints[tint]}`} data-testid={`analytics-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
           style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
        {value.toLocaleString()}
      </div>
      {sub && <div className="text-xs text-slate-600 mt-1">{sub}</div>}
    </div>
  );
}

function Sparkline({ points, className = "" }) {
  if (!points || points.length === 0) return null;
  const max = Math.max(...points.map((p) => p.views), 1);
  const w = 100;
  const h = 40;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(2)},${(h - (p.views / max) * h).toFixed(2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={`w-full h-14 ${className}`}
         data-testid="analytics-sparkline">
      <path d={path} fill="none" stroke="#2563eb" strokeWidth="1.5" />
      <path d={`${path} L${w},${h} L0,${h} Z`} fill="#2563eb22" />
    </svg>
  );
}

export default function AdminAnalytics() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth() || {};
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user || authLoading) return;
    if (!user.is_admin && user.platform_role !== "editor" && user.platform_role !== "super_admin") return;
    setData(null); setErr(null);
    fetchOverview(days).then(setData).catch((e) => setErr(e.message));
  }, [days, user, authLoading]);

  if (authLoading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!user) { nav("/auth?next=/admin/analytics"); return null; }
  const canSee = user.is_admin || user.platform_role === "editor" || user.platform_role === "super_admin";
  if (!canSee) return <div className="p-12 text-center text-slate-500" data-testid="analytics-403">Editors + admins only.</div>;

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-10" data-testid="admin-analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-1.5">
            Editorial · Traffic Overview
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
              style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
            Site Analytics
          </h1>
        </div>
        <div className="flex gap-1 rounded-full border border-slate-200 p-1 bg-white">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setDays(r.key)}
                    className={`px-3 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                      days === r.key ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"
                    }`}
                    data-testid={`analytics-range-${r.key}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900 mb-6">{err}</div>}

      {!data ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading overview…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard icon={BarChart3} label="Total views" value={data.total_views}
                      sub={`over ${data.days} day${data.days === 1 ? "" : "s"}`} tint="blue" />
            <StatCard icon={Users} label="Unique visitors" value={data.approx_unique_visitors}
                      sub="daily-salted IP hash approx." tint="green" />
            <StatCard icon={TrendingUp} label="Signed-in views" value={data.signed_in_views}
                      sub={data.total_views ? `${Math.round((data.signed_in_views / data.total_views) * 100)}% of total` : "—"} tint="amber" />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="analytics-timeseries">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600">Daily views</div>
              <div className="text-xs text-slate-500">{data.timeseries.length} day{data.timeseries.length === 1 ? "" : "s"}</div>
            </div>
            <Sparkline points={data.timeseries} />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>{data.timeseries[0]?.day || ""}</span>
              <span>{data.timeseries[data.timeseries.length - 1]?.day || ""}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-top-paths">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600 mb-3 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Top paths
              </div>
              {data.top_paths.length === 0 ? (
                <div className="text-sm text-slate-500 py-4">No traffic yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {data.top_paths.map((r, i) => (
                    <li key={r.path + i} className="flex items-center gap-3 text-sm">
                      <span className="text-slate-400 w-5 text-right shrink-0">{i + 1}</span>
                      <span className="flex-1 truncate font-medium text-slate-800">{r.path}</span>
                      <span className="text-slate-600 tabular-nums">{r.views.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-top-portals">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600 mb-3 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" /> Top portals
              </div>
              {data.top_portals.length === 0 ? (
                <div className="text-sm text-slate-500 py-4">No traffic yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {data.top_portals.map((r, i) => (
                    <li key={r.portal_slug + i} className="flex items-center gap-3 text-sm">
                      <span className="text-slate-400 w-5 text-right shrink-0">{i + 1}</span>
                      <span className="flex-1 truncate font-medium text-slate-800 capitalize">{r.portal_slug}</span>
                      <span className="text-slate-600 tabular-nums">{r.views.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
